import type { ProductFormData } from "../../../shared/schemas/product.schema";

type Option = { id: string };

/**
 * Chuyển sản phẩm từ products.getById sang giá trị của ProductForm (vật tư, công đoạn, cấu hình công đoạn theo thứ tự đã lưu).
 * Dùng chung cho trang Sửa sản phẩm và hộp "Chỉnh sửa sản phẩm" mở từ Báo giá / Đơn hàng — hộp sửa trước đây đưa nguyên
 * dữ liệu thô vào form nên form không thấy công đoạn và bắt nhập lại quy trình (FB-4T1R48).
 */
export function productToFormValues(
  product: any,
  { productTypeOptions, materialTypeOptions, processOptions }: { productTypeOptions: Option[]; materialTypeOptions: Option[]; processOptions: Option[] },
): Partial<ProductFormData> & Record<string, any> {
  const rawMaterialIds = product.productMaterials?.length
    ? product.productMaterials
        .sort(
          (a: any, b: any) =>
            (a.sortOrder ?? 999) - (b.sortOrder ?? 999),
        )
        .map((pm: any) => pm.materialId || pm.material?.id)
        .filter(Boolean)
    : product.materialId
      ? [product.materialId]
      : [];

  let specsObj: any = {};
  try {
    if (typeof product.specs === "object" && product.specs) {
      specsObj = { ...product.specs };
    } else if (typeof product.specs === "string" && product.specs.startsWith("{")) {
      specsObj = JSON.parse(product.specs);
    }
  } catch {}

  const hasMaterialTypesInSpecs = Array.isArray(specsObj.materialTypeIds) && specsObj.materialTypeIds.length > 0;
  if (!hasMaterialTypesInSpecs && rawMaterialIds.length === 0 && materialTypeOptions.length > 0) {
    specsObj.materialTypeIds = [materialTypeOptions[0].id];
  }

  const rawProcessIds = product.productProcesses?.length
    ? product.productProcesses
        .sort(
          (a: any, b: any) =>
            (a.sortOrder ?? 999) - (b.sortOrder ?? 999),
        )
        .map((pp: any) => pp.processId || pp.process?.id)
        .filter(Boolean)
    : [];

  const finalProcessIds = rawProcessIds.length > 0
    ? rawProcessIds
    : (processOptions.length > 0 ? [processOptions[0].id] : []);

  return {
    productCode: product.productCode,
    productName: product.productName,
    customerId: product.customerId ?? "",
    customerName:
      (product.customer?.shortName || product.customer?.name) ?? "",
    typeId: product.typeId || productTypeOptions[0]?.id || "",
    materialIds: rawMaterialIds,
    productMaterials: product.productMaterials?.length
      ? product.productMaterials.map((pm: any) => ({
          materialId: pm.materialId,
          processId: pm.process?.processId || "",
          standardQuantity: pm.standardQuantity || 0,
          note: pm.note || "",
        }))
      : [],
    unit: product.unit || "Cái",
    packingSpec: product.packingSpec ?? "",
    notes: product.notes ?? "",
    pcsPerBox: product.pcsPerBox ?? null,
    price: product.price ? Number(product.price) : 0,
    costPrice: product.costPrice ? Number(product.costPrice) : 0,
    length: product.length && Number(product.length) > 0 ? Number(product.length) : 100,
    width: product.width && Number(product.width) > 0 ? Number(product.width) : 100,
    height: product.height && Number(product.height) > 0 ? Number(product.height) : 50,
    flatLength: product.flatLength ?? (typeof product.specs === 'object' && product.specs?.flatLength ? product.specs.flatLength : undefined),
    flatWidth: product.flatWidth ?? (typeof product.specs === 'object' && product.specs?.flatWidth ? product.specs.flatWidth : undefined),
    printFormat: product.printFormat ?? "",
    cutFormat: product.cutFormat ?? "",
    outs: product.outs ?? undefined,
    process: product.process ?? "",
    processIds: finalProcessIds,
    processConfigs: product.productProcesses?.length
      ? product.productProcesses
          .sort(
            (a: any, b: any) =>
              (a.sortOrder ?? 999) - (b.sortOrder ?? 999),
          )
          .map((pp: any) => ({
            processId: pp.processId || pp.process?.id,
            setupTimeMinutes: pp.setupTimeMinutes,
            speedPerHour: pp.speedPerHour,
            runWastePercent: pp.runWastePercent,
            setupWasteQty: pp.setupWasteQty,
            isOutsourced: pp.isOutsourced,
            unitCost: pp.unitCost,
            batchCost: pp.batchCost,
          }))
          .filter((c: any) => c.processId)
      : [],
    currentVersion: product.currentVersion,
    specs: JSON.stringify(specsObj),
    image: product.image ?? "",
    tags: product.tags ?? "",
    active: product.active,
  };
}
